import { DocIdParams } from '../dto/documents.js';
import { listOf, PageQueryV1 } from '../dto/list.js';
import { MentionLinkRequestV1, MentionLinkResponseV1, PaletteQueryV1, PaletteResultV1, PickerQueryV1, PickerResultV1, UnlinkedOccurrenceV1, } from '../dto/search.js';
import { defineRoute } from '../route/define-route.js';
import { EXAMPLE_IDS } from './example-ids.js';
import { EXAMPLE_PICKER_RESULT } from './examples-docs.js';
import { EXAMPLE_PALETTE_RESULT, EXAMPLE_UNLINKED } from './examples-workspace.js';
/**
 * The `@` picker (checklist items 3 and 3b): canon hits, then the SRD group, with the exact-match
 * flags A2.1 section 4's picker rule reads. Read-only: reference documents may be returned.
 */
export const searchPicker = defineRoute({
    id: 'search.picker',
    method: 'GET',
    path: '/api/search/picker',
    query: PickerQueryV1,
    response: { kind: 'json', schema: PickerResultV1 },
    auth: 'session',
    smoke: 'read',
    reference: 'read',
    status: 'frozen',
    owner: '1.D',
    stage: '1',
    since: 'editor-alpha',
    examples: {
        request: { query: { q: 'Mael', typeKeys: 'npc,pc', limit: 8 } },
        response: EXAMPLE_PICKER_RESULT,
    },
});
/**
 * The palette (Mod-K): canon hits, then an SRD group capped at five with its total (A2.1 section
 * 4). Read-only: reference documents may be returned.
 */
export const searchPalette = defineRoute({
    id: 'search.palette',
    method: 'GET',
    path: '/api/search',
    query: PaletteQueryV1,
    response: { kind: 'json', schema: PaletteResultV1 },
    auth: 'session',
    smoke: 'read',
    reference: 'read',
    status: 'frozen',
    owner: '1.D',
    stage: '1b',
    since: 'exit-1a',
    examples: { request: { query: { q: 'lamp', limit: 20 } }, response: EXAMPLE_PALETTE_RESULT },
});
/**
 * The Unlinked tab: the other pages that name this page in plain text (canon only, A2.1 section
 * 4; DECISIONS entry 38 item 24), an unbounded list. A reference page answers an empty list.
 */
export const searchUnlinked = defineRoute({
    id: 'search.unlinked',
    method: 'GET',
    path: '/api/docs/:id/unlinked',
    params: DocIdParams,
    query: PageQueryV1,
    response: { kind: 'json', schema: listOf(UnlinkedOccurrenceV1) },
    auth: 'session',
    smoke: 'read',
    reference: 'read',
    docIds: { owner: ['params.id'] },
    status: 'frozen',
    owner: '1.D',
    stage: '1b',
    since: 'exit-1a',
    examples: {
        request: { params: { id: EXAMPLE_IDS.npcOldSef.id }, query: { limit: 50 } },
        response: { items: [EXAMPLE_UNLINKED], next: null },
    },
});
/**
 * "Link" (decision 31): wrap one occurrence in the source into a mention of this page. A stale
 * `occurrence.baseBodyVersion` answers 409 `version_conflict` (details `VersionConflictDetailsV1`,
 * `conflictRevNo: null`); a trashed source 409 `document_trashed`. Canon only: neither side may be
 * a reference document. The example succeeds on the demo template whether or not `doc_name` has
 * been derived.
 */
export const mentionsLink = defineRoute({
    id: 'mentions.link',
    method: 'POST',
    path: '/api/docs/:id/unlinked/link',
    params: DocIdParams,
    body: MentionLinkRequestV1,
    response: { kind: 'json', schema: MentionLinkResponseV1 },
    errors: { 409: ['version_conflict', 'document_trashed'] },
    auth: 'session',
    smoke: 'deny',
    reference: 'deny',
    docIds: { owner: ['params.id', 'body.sourceDocId'] },
    status: 'frozen',
    owner: '1.D',
    stage: '1b',
    since: 'exit-1a',
    examples: {
        request: {
            params: { id: EXAMPLE_IDS.npcOldSef.id },
            body: {
                sourceDocId: EXAMPLE_IDS.questMissingLamplighters.id,
                blockId: EXAMPLE_IDS.blockMissingLamplightersOldSef.id,
                occurrence: { ordinal: 0, baseBodyVersion: 1 },
            },
        },
        response: { sourceBodyVersion: 2 },
        consumes: true,
    },
});
//# sourceMappingURL=search.js.map